import { supabase } from '../config/supabase'

class SupabaseAuthService {
  // ============================================
  // Admin OAuth Login (Google/Gmail)
  // ============================================
  async signInWithGoogle() {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/admin`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      })

      if (error) throw error
      return { success: true, data }
    } catch (error) {
      console.error('Error signing in with Google:', error)
      return { success: false, error: error.message }
    }
  }

  // ============================================
  // Admin Username/Password Login
  // ============================================
  async adminLoginWithPassword(username, password) {
    const FUNCTIONS_URL = process.env.REACT_APP_SUPABASE_FUNCTIONS_URL ||
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1`

    // Step 1: Call the Edge Function to verify credentials and
    // get a real Supabase session.
    if (FUNCTIONS_URL) {
      try {
        const response = await fetch(`${FUNCTIONS_URL}/auth-admin-login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY || ''}`,
          },
          body: JSON.stringify({ username, password }),
        })

        const result = await response.json().catch(() => ({}))

        if (!response.ok || !result.success) {
          return { success: false, error: result.error || `Login failed (${response.status})` }
        }

        if (result.session) {
          return {
            success: true,
            user: result.user,
            session: result.session,
          }
        }

        return { success: false, error: 'Edge Function returned no session' }
      } catch (edgeError) {
        console.warn('Edge Function unreachable, falling back to direct auth:', edgeError.message)
      }
    }

    // Step 2: Fallback — direct Supabase Auth sign-in.
    const ADMIN_AUTH_EMAIL = process.env.REACT_APP_ADMIN_AUTH_EMAIL || 'admin@gdcbeekapur.org.in'
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: ADMIN_AUTH_EMAIL,
        password,
      })

      if (error) {
        console.error('Admin login error:', error.message)
        return { success: false, error: error.message || 'Invalid credentials' }
      }

      if (!data?.session) {
        return { success: false, error: 'Login succeeded but no session returned' }
      }

      return {
        success: true,
        user: data.user,
        session: data.session,
      }
    } catch (error) {
      console.error('Error in admin login:', error)
      return { success: false, error: error.message }
    }
  }

  // ============================================
  // Sign in with email/password (for admin session / RLS)
  // ============================================
  async signInWithPassword(email, password) {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) throw error
      return { success: true, data }
    } catch (error) {
      console.error('Error signing in with password:', error)
      return { success: false, error: error.message }
    }
  }

  // ============================================
  // Get Current Session (OAuth)
  // ============================================
  async getSession() {
    try {
      const { data: { session }, error } = await supabase.auth.getSession()

      if (error) throw error
      return { success: true, session }
    } catch (error) {
      console.error('Error getting session:', error)
      return { success: false, error: error.message }
    }
  }

  // ============================================
  // Get Current User (OAuth)
  // ============================================
  async getCurrentUser() {
    try {
      const { data: { user }, error } = await supabase.auth.getUser()

      if (error) throw error
      return { success: true, user }
    } catch (error) {
      console.error('Error getting current user:', error)
      return { success: false, error: error.message }
    }
  }

  // ============================================
  // Verify Admin Email (check admin_emails whitelist)
  // ============================================
  async verifyAdminEmail(email) {
    try {
      const { data, error } = await supabase
        .from('admin_emails')
        .select('emails')
        .eq('id', 'admin_list')
        .single()

      if (error || !data) {
        return { success: false, isAdmin: false, error: 'Admin list not found' }
      }

      const emails = Array.isArray(data.emails) ? data.emails : JSON.parse(String(data.emails))
      const isAdmin = emails.some(e => e.toLowerCase().trim() === email.toLowerCase().trim())

      return { success: true, isAdmin }
    } catch (error) {
      console.error('Error verifying admin email:', error)
      return { success: false, isAdmin: false, error: error.message }
    }
  }

  // ============================================
  // Sign Out
  // ============================================
  async signOut() {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      return { success: true }
    } catch (error) {
      console.error('Error signing out:', error)
      return { success: false, error: error.message }
    }
  }

  // ============================================
  // Listen to Auth State Changes
  // ============================================
  onAuthStateChange(callback) {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(callback)
    return {
      unsubscribe: () => subscription.unsubscribe
    }
  }
}

const supabaseAuthService = new SupabaseAuthService()
export default supabaseAuthService
